import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Outlet, Link } from "react-router-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuthStore } from "../../store/useAuthStore";
import { ORG_SWITCH_FLAG } from "../../constant";
import LogoutModal from "../../components/modal/LogoutModal";
import { Sidebar } from "../../components/layout/Sidebar";
import { useGetUserDevices } from "../../queries/admin";
import Banner from "../new/Banner";
import AddLocationModal from "../../components/modal/AddLocationModal";
import AddDeviceToLocationModal from "../../components/modal/AddDeviceToLocationModal";
import { EditModeContext } from "../../contexts/EditModeContext";
import { useQueryClient } from "@tanstack/react-query";
import { SupportDialog } from "../../components/ui/SupportDialog";
import OrgSwitcher from "../../components/layout/OrgSwitcher";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { useIsAdmin } from "../../utils/auth";
import {
  Settings,
  LogOut,
  LifeBuoy,
  Menu as MenuIcon,
  Smile,
  User,
  ShieldCheck,
  LayoutGrid,
  CreditCard,
} from "lucide-react";

export default function NewLayout() {
  const [dialogLogout, setDialogLogout] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const [showAddLocationModal, setShowAddLocationModal] = useState(false);
  const [showAddDeviceModal, setShowAddDeviceModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);

  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { data: user } = useGetUserDevices();

  const { role, email, name } = useAuthStore();
  const isAdmin = useIsAdmin();
  const queryClient = useQueryClient();

  // After an org switch the app reloads with this flag set; hold a
  // full-screen loader so the new org's data settles behind it. Living in
  // the layout makes the hold identical on every page.
  const [isOrgSwitchHold, setIsOrgSwitchHold] = useState(
    () => sessionStorage.getItem(ORG_SWITCH_FLAG) === "1",
  );
  useEffect(() => {
    if (!isOrgSwitchHold) return;
    sessionStorage.removeItem(ORG_SWITCH_FLAG);
    const timer = setTimeout(() => setIsOrgSwitchHold(false), 2000);
    return () => clearTimeout(timer);
  }, [isOrgSwitchHold]);

  useEffect(() => {
    if (pathname === "/") {
      navigate("/dashboard");
    }
  }, [navigate, pathname, role]);

  const handleLogout = () => {
    setDialogLogout(true);
  };

  const userInitial = (user?.name?.[0] ?? name?.[0] ?? email?.[0] ?? "U").toUpperCase();
  const displayName = user?.name || name || email || "User";

  return (
    <div className="h-screen bg-gray-100 dark:bg-gray-950 flex overflow-hidden">
      {/* Continues the OrgSwitcher's pre-reload loader after the page reloads */}
      {isOrgSwitchHold &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-white dark:bg-gray-900">
            <div className="relative">
              <div className="h-16 w-16 rounded-full border-4 border-gray-200 dark:border-gray-700" />
              <div className="absolute top-0 h-16 w-16 animate-spin rounded-full border-4 border-transparent border-t-primary" />
            </div>
            <p className="mt-4 text-sm font-medium text-gray-600 dark:text-gray-400">
              Switching organization...
            </p>
          </div>,
          document.body,
        )}

      {dialogLogout && <LogoutModal isOpen={dialogLogout} onClose={() => setDialogLogout(false)} />}

      {/* Customer support dialog (opened from the profile dropdown) */}
      <SupportDialog open={supportOpen} onClose={() => setSupportOpen(false)} />

      {showAddLocationModal && (
        <AddLocationModal
          isOpen={showAddLocationModal}
          onClose={() => setShowAddLocationModal(false)}
        />
      )}

      {showAddDeviceModal && (
        <AddDeviceToLocationModal
          isOpen={showAddDeviceModal}
          onClose={() => setShowAddDeviceModal(false)}
        />
      )}

      {/* Sidebar */}
      <Sidebar
        onToggle={setSidebarExpanded}
        mobileOpen={mobileMenuOpen}
        onMobileClose={() => setMobileMenuOpen(false)}
        mobileFooter={
          <div className="flex flex-col w-full gap-3">
            {/* User profile section */}
            <div className="flex items-center gap-3 px-2 py-2 rounded-lg bg-gray-50 dark:bg-gray-800">
              <div className="h-10 w-10 shrink-0 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-gray-700 dark:text-gray-200 font-semibold text-base">
                {userInitial}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                  {displayName}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  {email || "user@example.com"}
                </p>
              </div>
            </div>

            {/* Action links */}
            <nav className="flex flex-col">
              <SupportDialog
                trigger={
                  <button className="flex items-center gap-3 px-2 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg transition-colors">
                    <LifeBuoy className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                    Support
                  </button>
                }
              />
              <Link
                to="/settings"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-2 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg transition-colors"
              >
                <Settings className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                Settings
              </Link>
              <button
                onClick={() => {
                  handleLogout();
                  setMobileMenuOpen(false);
                }}
                className="flex items-center gap-3 px-2 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
              >
                <LogOut className="h-5 w-5" />
                Sign out
              </button>
            </nav>
          </div>
        }
      />

      <div className="flex flex-col flex-1 min-h-0">
        {(user?.client?.showBanner ?? false) && (
          <Banner
            bannerMessage={user?.client?.bannerMessage ?? ""}
            bannerLink={user?.client?.bannerLink ?? ""}
          />
        )}

        <main
          className={`flex-1 flex flex-col overflow-y-auto transition-all duration-300 ${
            sidebarExpanded ? "ml-0 md:ml-72" : "ml-0 md:ml-20"
          }`}
        >
          {/* ============ TOP NAVBAR START ============ */}
          <div className="sticky top-0 z-30 bg-gray-100 dark:bg-gray-950 px-4 pt-4 pb-3 sm:px-6 lg:px-8">
            {/* Row 1: Mobile menu, spacer, support, profile */}
            <div className="flex items-center gap-4">
              {/* Mobile menu button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-white dark:hover:bg-gray-800 md:hidden"
              >
                <MenuIcon className="h-6 w-6" />
              </button>

              <div className="flex-1" />

              {/* Organization switcher - visible across all pages */}
              <div className="shrink-0">
                <OrgSwitcher />
              </div>

              {/* Profile dropdown - desktop only */}
              <Menu as="div" className="relative shrink-0 hidden md:block">
                <MenuButton className="relative flex items-center gap-2 pl-3 pr-1.5 h-10 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:shadow-md transition-all focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600">
                  <span className="sr-only">Open user menu</span>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300 max-w-[120px] truncate">
                    {displayName.slice(0, 8)}
                  </span>
                  <div className="h-7 w-7 rounded-md bg-blue-600 dark:bg-blue-600 flex items-center justify-center text-white font-medium text-sm border border-blue-600 dark:border-blue-600">
                    {userInitial}
                  </div>
                </MenuButton>

                <MenuItems
                  transition
                  className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white dark:bg-gray-800 py-1 shadow-lg transition data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[enter]:ease-out data-[leave]:duration-75 data-[leave]:ease-in"
                >
                  <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                      {displayName}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {email || "user@example.com"}
                    </p>
                  </div>
                  <div className="border-t border-gray-100 dark:border-gray-700 my-1" />
                  <div className="px-4 py-1.5">
                    <p className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                      Settings
                    </p>
                  </div>
                  <MenuItem>
                    <Link
                      to="/settings?tab=Profile"
                      className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 data-[focus]:bg-gray-100 dark:data-[focus]:bg-gray-700 data-[focus]:outline-hidden"
                    >
                      <User className="mr-3 h-4 w-4 text-gray-400 dark:text-gray-500" />
                      Profile
                    </Link>
                  </MenuItem>
                  {isAdmin && (
                    <>
                      <MenuItem>
                        <Link
                          to="/settings?tab=Access"
                          className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 data-[focus]:bg-gray-100 dark:data-[focus]:bg-gray-700 data-[focus]:outline-hidden"
                        >
                          <ShieldCheck className="mr-3 h-4 w-4 text-gray-400 dark:text-gray-500" />
                          Access
                        </Link>
                      </MenuItem>
                      <MenuItem>
                        <Link
                          to="/settings?tab=Widgets"
                          className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 data-[focus]:bg-gray-100 dark:data-[focus]:bg-gray-700 data-[focus]:outline-hidden"
                        >
                          <LayoutGrid className="mr-3 h-4 w-4 text-gray-400 dark:text-gray-500" />
                          Widgets
                        </Link>
                      </MenuItem>
                      <MenuItem>
                        <Link
                          to="/settings?tab=Billing"
                          className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 data-[focus]:bg-gray-100 dark:data-[focus]:bg-gray-700 data-[focus]:outline-hidden"
                        >
                          <CreditCard className="mr-3 h-4 w-4 text-gray-400 dark:text-gray-500" />
                          Billing
                        </Link>
                      </MenuItem>
                    </>
                  )}
                  <div className="border-t border-gray-100 dark:border-gray-700 my-1" />
                  <MenuItem>
                    <button
                      onClick={() => setSupportOpen(true)}
                      className="flex w-full items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 data-[focus]:bg-gray-100 dark:data-[focus]:bg-gray-700 data-[focus]:outline-hidden"
                    >
                      <LifeBuoy className="mr-3 h-4 w-4 text-gray-400 dark:text-gray-500" />
                      Customer Support
                    </button>
                  </MenuItem>
                  <MenuItem>
                    <button
                      onClick={handleLogout}
                      className="flex w-full items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 data-[focus]:bg-gray-100 dark:data-[focus]:bg-gray-700 data-[focus]:outline-hidden"
                    >
                      <LogOut className="mr-3 h-5 w-5 text-gray-400 dark:text-gray-500" />
                      Sign out
                    </button>
                  </MenuItem>
                </MenuItems>
              </Menu>
            </div>

            {/* Row 2: Page-specific content (title, org switcher, etc.) */}
            <div id="navbar-slot" className="flex-1 flex items-center gap-3 min-w-0 mt-4" />

            {/* Row 3: Page-specific filters — kept in the sticky header so they don't scroll away */}
            <div id="navbar-filters-slot" />
          </div>
          {/* ============ TOP NAVBAR END ============ */}

          {/* Main Content */}
          <div className="flex-1 flex flex-col px-4 pb-6 sm:px-6 lg:px-8">
            <EditModeContext.Provider value={{ isEditMode: editMode, setEditMode }}>
              <Outlet />
            </EditModeContext.Provider>
          </div>
        </main>
      </div>
    </div>
  );
}
